import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import FloatingCart from "@/components/FloatingCart";
import { api, trackWaClick } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Wifi, MapPin, Gift, Zap, BadgePercent, CheckCircle2, XCircle, ArrowLeft, Loader2, MessageCircle, Handshake, Server, Banknote, TicketPercent, PlugZap, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/rkot18ba_Logo%20PT.%20Fasnet%20Sky%20Fiber.png";
const COVERAGE_KM = 5;
const WA_ADMIN = "6281377114000";

const MITRA_BENEFITS = [
  { icon: Server, title: "Tempat Mini Server", desc: "Rumahmu menjadi titik Mini Server FASNET WiFi di lingkunganmu." },
  { icon: Wifi, title: "Gratis Internet", desc: "Internet FASNET gratis selama kamu menjadi Mitra aktif." },
  { icon: Banknote, title: "Payment Point", desc: "Komisi Rp 10.000 per konsumen yang bayar tagihan di tempatmu." },
  { icon: TicketPercent, title: "Jual Voucher WiFi", desc: "Komisi 25% dari setiap harga voucher yang berhasil dijual." },
  { icon: PlugZap, title: "Bantuan Token Listrik", desc: "Rp 100.000/bulan untuk operasional mini server di rumahmu." },
];

export default function Fasnet() {
  const [distance, setDistance] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "", referral_name: "" });
  const [loading, setLoading] = useState(false);
  const [mitraForm, setMitraForm] = useState({ name: "", phone: "", address: "", notes: "" });
  const [mitraLoading, setMitraLoading] = useState(false);
  const [mitraSent, setMitraSent] = useState(null);

  const submitMitra = async () => {
    if (!mitraForm.name.trim() || !mitraForm.phone.trim() || !mitraForm.address.trim()) {
      return toast.error("Lengkapi nama, nomor HP, dan alamat");
    }
    setMitraLoading(true);
    try {
      await api.post("/fasnet/register", { ...mitraForm, apply_type: "mitra" });
      toast.success("Pengajuan Mitra terkirim!", { description: "Tim FASNET akan menghubungi kamu. Lanjut ke WA Admin untuk respon lebih cepat." });
      setMitraSent(mitraForm);
      setMitraForm({ name: "", phone: "", address: "", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pengajuan");
    } finally {
      setMitraLoading(false);
    }
  };

  const checkArea = () => {
    const km = parseFloat(distance);
    if (isNaN(km) || km < 0) return toast.error("Masukkan perkiraan jarak yang valid (km)");
    setCheckResult(km <= COVERAGE_KM ? "covered" : "outside");
  };

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      return toast.error("Lengkapi nama, nomor HP, dan alamat pemasangan");
    }
    setLoading(true);
    try {
      await api.post("/fasnet/register", form);
      toast.success("Pendaftaran terkirim!", { description: "Tim FASNET akan menghubungi kamu via WhatsApp untuk jadwal survey." });
      setForm({ name: "", phone: "", address: "", referral_name: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pendaftaran");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <CartDrawer />
      <FloatingCart />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-700/25 hover:bg-emerald-800 hover:shadow-lg hover:-translate-y-0.5 transition">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-blue-800 to-blue-950 text-white p-8 md:p-12"
        >
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(249,115,22,0.45), transparent 40%), radial-gradient(circle at 85% 80%, rgba(59,130,246,0.4), transparent 45%)" }} />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="tag-pill bg-orange-400 text-blue-950">
                <Wifi className="w-3 h-3" /> FASNET WiFi
              </span>
              <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Internet fiber <span className="text-orange-300">untuk semua.</span>
              </h1>
              <p className="mt-5 text-blue-100/90 text-sm md:text-base leading-relaxed max-w-md">
                WiFi kencang 10 Mbps cuma Rp 150.000/bulan. Daftar & bayar tagihan gampang lewat warung FASMART tetangga kamu.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="#daftar-fasnet"
                  data-testid="fasnet-register-cta"
                  className="px-6 py-3 rounded-full bg-orange-400 text-blue-950 font-bold hover:bg-orange-300 transition shadow-lg shadow-orange-400/30"
                >
                  Daftar Pemasangan →
                </a>
                <a href="#cek-area" className="px-6 py-3 rounded-full bg-white/10 border border-white/20 font-semibold hover:bg-white/20 transition">
                  Cek Area Coverage
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-widest text-blue-100/80">
                <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> 10 Mbps unlimited</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Cover 5 km dari kantor</span>
                <span className="flex items-center gap-1.5"><BadgePercent className="w-3.5 h-3.5" /> Diskon pasang 20%</span>
              </div>
            </div>
            <div className="bg-white rounded-3xl p-6 md:p-8">
              <img src={LOGO} alt="FASNET Fiber" className="w-full max-w-sm mx-auto object-contain" data-testid="fasnet-logo" />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Paket & Promo */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Paket & Promo Bulan Ini</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="md:col-span-1 rounded-3xl bg-white border border-[#E5DECF] p-6" data-testid="fasnet-package-card">
            <Wifi className="w-7 h-7 text-blue-700" />
            <h3 className="mt-3 font-display font-bold text-xl">Paket WiFi 10 Mbps</h3>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-display font-black text-3xl text-blue-700">Rp 150.000</span>
              <span className="text-sm text-stone-500">/bulan</span>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-stone-600">
              <li>✓ Unlimited tanpa FUP</li>
              <li>✓ Cocok streaming & WFH</li>
              <li>✓ Bayar tagihan di warung</li>
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }}
            className="md:col-span-1 rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6" data-testid="fasnet-promo-card">
            <BadgePercent className="w-7 h-7" />
            <h3 className="mt-3 font-display font-bold text-xl">GRATIS Pemasangan Bulan Ini</h3>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display font-black text-3xl">Rp 0</span>
              <span className="text-sm line-through opacity-70">Rp 150.000</span>
            </div>
            <p className="text-xs opacity-90 mt-3">Cukup bayar Rp 150.000 (paket bulan pertama) saat pemasangan — langsung online 1 bulan. Sudah termasuk router + instalasi kabel fiber ke rumah.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.16 }}
            className="md:col-span-1 rounded-3xl bg-gradient-to-br from-amber-400 to-amber-500 text-stone-900 p-6" data-testid="fasnet-referral-card">
            <Gift className="w-7 h-7" />
            <h3 className="mt-3 font-display font-bold text-xl">Program Ajak Tetangga</h3>
            <p className="text-sm mt-2">Ajak tetangga pasang FASNET WiFi, dapatkan hadiah langsung:</p>
            <div className="mt-3 rounded-2xl bg-white/60 px-4 py-3 font-bold text-sm">
              🎁 ½ Kg Gula Pasir + 5 Bungkus Indomie Kaldu
            </div>
            <p className="text-xs mt-2 opacity-80">Cantumkan nama kamu sebagai pengajak saat tetanggamu mendaftar.</p>
          </motion.div>
        </div>
      </section>

      {/* Promo Jadi Mitra */}
      <section id="mitra-fasnet" className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="fasnet-mitra-section">
        <div className="flex items-center gap-2">
          <Handshake className="w-6 h-6 text-blue-700" />
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">MAU Jadi Mitra FASNET WiFi?</h2>
        </div>
        <p className="text-stone-600 mt-1 max-w-2xl">Rumahmu bisa jadi sumber penghasilan tambahan. Ajukan diri jadi Mitra dan nikmati 5 benefit ini:</p>
        <div data-testid="fasnet-mitra-syarat" className="mt-4 inline-flex items-start gap-2.5 rounded-2xl bg-orange-50 border-2 border-orange-300 px-5 py-3.5 text-sm md:text-base text-orange-900">
          <ClipboardList className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
          <span><b>Syarat menjadi Mitra FASNET WiFi:</b> Daftarkan <b>30 Rumah Warga</b> yang akan berlangganan FASNET WiFi.</span>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {MITRA_BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className="rounded-3xl bg-white border border-[#E5DECF] p-5"
              data-testid={`fasnet-mitra-benefit-${i}`}
            >
              <b.icon className="w-7 h-7 text-blue-700" />
              <h3 className="mt-3 font-display font-bold text-base leading-tight">{b.title}</h3>
              <p className="mt-1.5 text-xs text-stone-600 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 rounded-3xl bg-gradient-to-br from-blue-700 to-blue-950 text-white p-8 md:p-10">
          <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight">Form Pengajuan Mitra</h3>
          <p className="text-blue-100/90 mt-2 max-w-xl">Isi data di bawah — tim FASNET akan menghubungi via WhatsApp untuk verifikasi & survey lokasi mini server.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            <div>
              <Label className="text-blue-100">Nama Lengkap</Label>
              <Input data-testid="fasnet-mitra-form-name" value={mitraForm.name} onChange={(e) => setMitraForm({ ...mitraForm, name: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="Nama sesuai KTP" />
            </div>
            <div>
              <Label className="text-blue-100">Nomor HP / WhatsApp</Label>
              <Input data-testid="fasnet-mitra-form-phone" value={mitraForm.phone} onChange={(e) => setMitraForm({ ...mitraForm, phone: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="0812xxxxxxxx" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-blue-100">Alamat Rumah (Lokasi Mini Server)</Label>
              <Textarea data-testid="fasnet-mitra-form-address" value={mitraForm.address} onChange={(e) => setMitraForm({ ...mitraForm, address: e.target.value })} className="mt-1 bg-white text-stone-900" rows={2} placeholder="Jalan, RT/RW, patokan rumah..." />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-blue-100">Catatan (opsional)</Label>
              <Input data-testid="fasnet-mitra-form-notes" value={mitraForm.notes} onChange={(e) => setMitraForm({ ...mitraForm, notes: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="Mis. daya listrik rumah, perkiraan jumlah tetangga yang berminat" />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              data-testid="fasnet-mitra-submit-btn"
              onClick={submitMitra}
              disabled={mitraLoading}
              className="rounded-full bg-orange-400 text-blue-950 hover:bg-orange-300 font-bold px-8 py-6 text-base"
            >
              {mitraLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengirim...</> : "Ajukan Diri Jadi Mitra"}
            </Button>
            {mitraSent && (
              <a
                data-testid="fasnet-mitra-wa-btn"
                onClick={() => trackWaClick("fasnet")}
                href={`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent(`Halo Admin FAS Group, saya ${mitraSent.name} (${mitraSent.phone}) baru mengajukan diri menjadi Mitra FASNET WiFi. Alamat: ${mitraSent.address}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-400 transition"
              >
                <MessageCircle className="w-4 h-4" /> Lanjut ke WA Admin
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Cek Area */}
      <section id="cek-area" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-3xl bg-white border border-[#E5DECF] p-8">
          <div className="flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-700" />
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight text-stone-900">Cek Area Coverage</h2>
          </div>
          <p className="text-sm text-stone-600 mt-1">FASNET WiFi menjangkau radius <b>5 km</b> dari Kantor Fasnet WiFi. Masukkan perkiraan jarak rumahmu.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 max-w-lg">
            <div className="flex-1">
              <Input
                data-testid="fasnet-distance-input"
                type="number"
                min="0"
                step="0.1"
                value={distance}
                onChange={(e) => { setDistance(e.target.value); setCheckResult(null); }}
                placeholder="Jarak dari kantor (km), mis. 2.5"
              />
            </div>
            <Button data-testid="fasnet-check-area-btn" onClick={checkArea} className="rounded-full bg-blue-700 hover:bg-blue-800 font-bold px-6">
              Cek Coverage
            </Button>
          </div>
          {checkResult === "covered" && (
            <div data-testid="fasnet-area-result-covered" className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div>
                <div className="font-bold text-emerald-800">Rumahmu tercover FASNET WiFi! 🎉</div>
                <div className="text-sm text-emerald-700">Langsung daftar pemasangan di bawah — diskon 20% masih berlaku.</div>
              </div>
            </div>
          )}
          {checkResult === "outside" && (
            <div data-testid="fasnet-area-result-outside" className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 p-4 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-rose-600 shrink-0" />
              <div>
                <div className="font-bold text-rose-800">Belum tercover untuk saat ini</div>
                <div className="text-sm text-rose-700">Jarak melebihi 5 km dari kantor. Titip nomor HP-mu di warung — kami kabari saat jaringan diperluas.</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Form Pendaftaran */}
      <section id="daftar-fasnet" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-3xl bg-gradient-to-br from-blue-700 to-blue-950 text-white p-8 md:p-10">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Form Pendaftaran Pemasangan</h2>
          <p className="text-blue-100/90 mt-2 max-w-xl">Isi data di bawah — tim FASNET akan hubungi via WhatsApp untuk jadwal survey & pemasangan.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            <div>
              <Label className="text-blue-100">Nama Lengkap</Label>
              <Input data-testid="fasnet-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="Nama sesuai KTP" />
            </div>
            <div>
              <Label className="text-blue-100">Nomor HP / WhatsApp</Label>
              <Input data-testid="fasnet-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="0812xxxxxxxx" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-blue-100">Alamat Pemasangan</Label>
              <Textarea data-testid="fasnet-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 bg-white text-stone-900" rows={2} placeholder="Jalan, RT/RW, patokan rumah..." />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-blue-100">Nama Tetangga yang Mengajak (opsional)</Label>
              <Input data-testid="fasnet-form-referral" value={form.referral_name} onChange={(e) => setForm({ ...form, referral_name: e.target.value })} className="mt-1 bg-white text-stone-900" placeholder="Isi agar pengajak dapat 🎁 ½ kg gula + 5 Indomie" />
            </div>
          </div>
          <Button
            data-testid="fasnet-form-submit-btn"
            onClick={submit}
            disabled={loading}
            className="mt-6 rounded-full bg-orange-400 text-blue-950 hover:bg-orange-300 font-bold px-8 py-6 text-base"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengirim...</> : "Kirim Pendaftaran"}
          </Button>
        </div>
      </section>

      <footer className="relative border-t border-[#E5DECF] py-6 text-center text-xs text-stone-500 font-mono uppercase tracking-widest">
        <a data-testid="footer-wa-link" onClick={() => trackWaClick("fasnet")} href="https://wa.me/6281377114000?text=Halo%20Admin%20FAS%20Group%2C%20saya%20mau%20tanya%20FASNET..." target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold normal-case tracking-normal hover:bg-emerald-800 transition">
          <MessageCircle className="w-4 h-4" /> Admin FAS Group Corp.
        </a>
        <div>FASNET WiFi · Internet Untuk Semua · Daftar &amp; bayar via FASMART</div>
        <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="mx-auto mt-4 h-14 w-auto object-contain rounded-xl bg-white p-1.5 border border-[#E5DECF] md:absolute md:right-6 md:bottom-4 md:m-0 md:h-16" />
      </footer>
    </div>
  );
}
